
import React, { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { NavLink } from './navigation/NavLink';
import { useInView } from 'framer-motion';
import { Footer } from './Footer';
import { useMediaQuery } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { VerificationBanner } from "./auth/VerificationBanner";
import { MobileHeader } from './layout/MobileHeader';
import { DesktopNav } from './layout/DesktopNav';
import { SimpleMobileMenu } from './layout/SimpleMobileMenu';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, isHost, onLogout, session } = useAuth();
  const headerRef = React.useRef<HTMLElement>(null);
  const isHeaderInView = useInView(headerRef, { once: false });
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  React.useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (!children) {
    return (
      <div className="flex min-h-screen flex-col">
        <div className="flex-1 flex items-center justify-center">
          <h1 className="text-2xl font-bold">Welcome to Travilis</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      {isAuthenticated && session?.user && !session.user.email_confirmed_at && (
        <VerificationBanner />
      )}
      
      <header
        ref={headerRef}
        className={cn(
          "sticky top-0 z-40 w-full border-b backdrop-blur transition-all duration-200",
          isScrolled 
            ? "bg-white/95 shadow-sm" 
            : "bg-white/80"
        )}
      >
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex items-center">
            <NavLink to="/">
              <div className="flex items-center space-x-2">
                <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  <span className="t-logo">t</span>ravilis
                </span>
              </div>
            </NavLink>
          </div>

          <DesktopNav 
            isAuthenticated={isAuthenticated}
            isHost={isHost}
            onLogout={onLogout}
          />

          <MobileHeader 
            isAuthenticated={isAuthenticated}
            isHost={isHost}
            onLogout={onLogout}
            isMobileMenuOpen={isMobileMenuOpen}
            toggleMobileMenu={toggleMobileMenu}
          />
        </div>
      </header>

      <SimpleMobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        isHost={isHost}
        onLogout={onLogout}
      />
      
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
